import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Lock } from 'lucide-react';
import { warehouseAPI } from '../../api/warehouses';
import { Warehouse } from '../../types/entities';

interface CreateLockModalProps {
  warehouses: Warehouse[];
  onClose: () => void;
  onSuccess: () => void;
}

const CreateLockModal: React.FC<CreateLockModalProps> = ({
  warehouses,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [warehouseId, setWarehouseId] = useState<number>(0);
  const [periodMonth, setPeriodMonth] = useState<number>(new Date().getMonth() + 1);
  const [periodYear, setPeriodYear] = useState<number>(new Date().getFullYear());
  const [lockReason, setLockReason] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!warehouseId) {
      setError(t('validation.required', { field: t('warehouse.periodLock.warehouse') }));
      return;
    }

    if (!lockReason.trim()) {
      setError(t('warehouse.periodLock.reasonRequired'));
      return;
    }

    setLoading(true);
    try {
      await warehouseAPI.createPeriodLock({
        warehouseId,
        periodYear,
        periodMonth,
        lockReason: lockReason.trim(),
      });

      onSuccess();
    } catch (err: any) {
      console.error('Failed to create period lock:', err);
      setError(err.response?.data?.message || t('common.errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Lock className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-800">
              {t('warehouse.periodLock.createLock')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Error Message */}
          {error && (
            <div className="px-4 py-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Warehouse Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('warehouse.periodLock.warehouse')} <span className="text-red-500">*</span>
            </label>
            <select
              value={warehouseId}
              onChange={(e) => setWarehouseId(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value={0}>{t('common.select')}</option>
              {warehouses.map((warehouse) => (
                <option key={warehouse.id} value={warehouse.id}>
                  {warehouse.code} - {warehouse.name}
                </option>
              ))}
            </select>
          </div>

          {/* Period Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('warehouse.periodLock.month')} <span className="text-red-500">*</span>
              </label>
              <select
                value={periodMonth}
                onChange={(e) => setPeriodMonth(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              >
                {[...Array(12)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {String(i + 1).padStart(2, '0')}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('warehouse.periodLock.year')} <span className="text-red-500">*</span>
              </label>
              <select
                value={periodYear}
                onChange={(e) => setPeriodYear(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              >
                {[...Array(5)].map((_, i) => {
                  const year = new Date().getFullYear() - i;
                  return (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          {/* Lock Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('warehouse.periodLock.reason')} <span className="text-red-500">*</span>
            </label>
            <textarea
              value={lockReason}
              onChange={(e) => setLockReason(e.target.value)}
              placeholder={t('warehouse.periodLock.reasonRequired')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
              rows={3}
              required
            />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              disabled={loading}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? t('common.processing') : t('warehouse.periodLock.createLock')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateLockModal;
